// @flow

import * as Sentry from '@sentry/browser';
import Jwt from 'jsonwebtoken';
import { AppConfig } from 'Config';
import { CacheResolvers } from 'LocalStates';
import { apolloClient } from './ApolloClient';
import GqlError, { Errors } from './GqlError';

const ORIGIN = 'business';

type AuthPayloadType = {
  token: string,
  refreshToken: string,
};

type CheckEmailPayloadType = {
  found: boolean,
  standardSSO: ?('saml' | 'oauth'),
  idSSO: ?string,
};

const post = async (path: string, body: {}): Promise<any> => {
  let response;

  try {
    response = await fetch(`${AppConfig.urls.ectorAuth}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-ECTOR-ORIGIN': ORIGIN,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    Sentry.captureException(e);

    throw new GqlError({ code: Errors.network, errors: [], data: {} });
  }

  let data;
  try {
    data = (await response.json()) || {};
  } catch (e) {
    data = {};
  }

  if (!response.ok) {
    const code = data.code || Errors.errorOccurred;

    if (!data.code) {
      Sentry.captureMessage(`${path} responded ${response.status}`, { extra: data });
    }

    throw new GqlError({ code, errors: [], data: {} });
  }

  return data;
};

const storeToken = async ({ token }: AuthPayloadType) => {
  if (!token || !Jwt.decode(token)) {
    Sentry.captureMessage('ector-auth returned no usable token');

    throw new GqlError({ code: Errors.errorOccurred, errors: [], data: {} });
  }

  await CacheResolvers.Mutation.login(undefined, { token }, { cache: apolloClient.cache });
};

export const checkEmail = (email: string): Promise<CheckEmailPayloadType> =>
  post('/check-email', { email });

export const login = async (email: string, password: string): Promise<AuthPayloadType> => {
  const payload = await post('/login', { email, password, origin: ORIGIN });
  await storeToken(payload);

  return payload;
};

export const swapToken = async (ssoToken: string): Promise<AuthPayloadType> => {
  const payload = await post('/swap-token', { sso_token: ssoToken, origin: ORIGIN });
  await storeToken(payload);

  return payload;
};

export const getAuthError = (t: string => string, error: Error): ErrorType => {
  const translationKey =
    error instanceof GqlError && !error.translationKey.startsWith('apiError')
      ? error.translationKey
      : 'errorOccurred';

  return { translationKey, message: t(`gql_errors:${translationKey}`) };
};
